import React, { useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { colors } from "styles/color-tokens";

import { FileSelector } from "../../file-selector/file-selector";
import {
  filtersChanged,
  userFilesSelected,
  filtersReset,
  pageSizeChanged,
  setPage,
  useMediaPickerGate,
  useMediaPickerModel,
} from "features/media-library/components/media-picker/model";
import { selectCollection } from "actions/mediaLibrary";

import { CollectionSelectorModern } from "features/media-library/components/collection-selector/collection-selector-modern";

import {
  MediaFilesFilters,
  useMediaFilesFilters,
} from "@/entities/media-files";
import { useMediaCollections } from "@/entities/media-collections";
import { MLSortBy } from "features/media-library/components/ml-sort-by";
import { MLToggleView } from "features/media-library/components/ml-toggle-view";
import { LibraryPayWall } from "features/media-library/components/library-pay-wall";
import { isFullLibrary } from "features/media-library";
import { useUGCUpsellModal } from "features/plans-modal";
import { Alert } from "ui";
import { AlertType } from "ui/alert";
import { CompactPagination } from "../compact-pagination";
import { Button, FormSize } from "ui/ecosystems/forms";
import { CollectionFilesList } from "features/media-library/components/collection-files-list";
import { CollectionFilesTable } from "features/media-library/components/files-table";
import { useCurrentUser } from "@/entities/current-user";
import { LoadingCover } from "ui/cover";
import { PickerProps } from "features/media-library/components/media-picker/media-picker";
import { MediaFilesSelectedFilters } from "@/entities/media-files/componets/filters/media-files-filters";

export function LibraryPicker(props: PickerProps) {
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const { isLoading: pendingCollections } = useMediaCollections();
  useMediaPickerGate();

  const { showUGCUpsellModal } = useUGCUpsellModal();
  const [viewType, setViewType] = useState<"list" | "grid">("grid");

  const {
    uploadingStatus,
    uploadingProgressValue,
    currentPage,
    pageSize,
    totalItems,
    currentCollection,
    collectionsTags,
    collections,
    media,
    filters,
  } = useMediaPickerModel();

  const handlePaginationChange = (page: number, newPageSize: number) => {
    if (newPageSize === pageSize) {
      dispatch(setPage(page));
    } else {
      dispatch(pageSizeChanged(newPageSize));
    }
  };

  const [filtersProps] = useMediaFilesFilters({
    values: filters,
    onChange: (nextFilters) => dispatch(filtersChanged(nextFilters)),
    tags: collectionsTags,
    onClear: () => dispatch(filtersReset()),
  });

  return (
    <LoadingCover isLoading={pendingCollections || !currentCollection}>
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
        {/* Left section */}
        <div className="flex items-center gap-2">
          <LibraryPayWall>
            {({ isDisabled }) => (
              <CollectionSelectorModern
                selectedCollection={currentCollection ?? null}
                collections={collections}
                onChange={(collectionId) =>
                  dispatch(selectCollection(collectionId, true))
                }
                disabled={isDisabled}
              />
            )}
          </LibraryPayWall>
          <MediaFilesFilters
            {...filtersProps}
            availableFilters={[
              "name",
              "description",
              "sourceName",
              "tags",
              "favorite",
              "mediaType",
              "usage",
            ]}
          />
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          <LibraryPayWall>
            {({ isDisabled }) => (
              <MLSortBy
                disabled={isDisabled}
                value={filters.sort}
                onChange={(sort) =>
                  dispatch(
                    filtersChanged({
                      ...filters,
                      sort,
                    })
                  )
                }
              />
            )}
          </LibraryPayWall>
          <MLToggleView value={viewType} onChange={setViewType} />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <MediaFilesSelectedFilters {...filtersProps} />
      </div>
      <FileSelectorContainer>
        <FileSelector
          uploadingStatus={uploadingStatus}
          uploadingProgressValue={uploadingProgressValue}
          limit={props.limit}
          isDisabled={props.limit <= props.selectedMediaFiles.size}
          onSelect={(files) =>
            dispatch(
              userFilesSelected(files, {
                onFileUploaded: props.handleFileAdd,
              }) as never
            )
          }
          title={`Upload into ${currentCollection?.name}`}
          renderOverlay={(props) => (
            <Overlay {...props}>
              <DropZoneTitle>Drop files to add to your post</DropZoneTitle>
            </Overlay>
          )}
        />
      </FileSelectorContainer>
      <Gallery>
        <Files>
          {React.createElement(views[viewType], {
            files: media,
            selectedMediaSet: props.selectedMediaFiles,
            selectedCollection: props.selectedCollection,
            onSelect: props.handleFileAdd,
            onRemove: props.handleFileRemove,
          })}
          {isFullLibrary(user) ? (
            <CompactPagination
              numItems={totalItems}
              currentPage={currentPage}
              pageSize={pageSize}
              pageSizeOptions={[12, 24, 48, 96]}
              onChange={handlePaginationChange}
            />
          ) : (
            <div style={{ marginTop: 12 }}>
              <Alert type={AlertType.info}>
                Upgrade your plan to view, sort and create posts from all
                assets. Upgraded plans can quickly source and save user
                generated content from hashtag searches, other users and more.
              </Alert>
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  size={FormSize.large}
                  primary
                  type="button"
                  onClick={() => showUGCUpsellModal()}
                >
                  Upgrade your plan
                </Button>
              </div>
            </div>
          )}
        </Files>
      </Gallery>
    </LoadingCover>
  );
}

const views = {
  grid: CollectionFilesList,
  list: CollectionFilesTable,
};

const Files = styled.div`
  /* Pagination styles */
  margin-top: 20px;
`;

const Gallery = styled.div``;

const FileSelectorContainer = styled.div``;

const DropZoneTitle = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 29px;
  color: ${colors.black};
`;

const Overlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${colors.neutral400};
  box-sizing: border-box;
  border-radius: 5px;

  &:before {
    content: "";
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: ${colors.white}E6;
    backdrop-filter: blur(10px);
    z-index: -1;
  }
`;
